import { merge, compact } from 'lodash'
import { useCallback, useMemo, useRef } from 'react'
import { useAuthentication } from '../../contexts/AuthenticationContext.jsx'
import fetchJSON from './fetchJSON.js'
import toUrlSearchParams from './toUrlSearchParams.js'
import deepSnakeizeKeys from "../utils/object/deepSnakeizeKeys.js";

function jsonToFormData(data, parentKey = null, formData = new FormData()) {
  const snakeCaseData = deepSnakeizeKeys(data);
  for (const key in snakeCaseData) {
    if (!Object.prototype.hasOwnProperty.call(snakeCaseData, key)) continue

    const value = snakeCaseData[key]
    const formKey = parentKey ? `${parentKey}[${key}]` : key

    if (value instanceof File || value instanceof Blob) {
      formData.append(formKey, value)
    } else if (value !== null && typeof value === 'object') {
      jsonToFormData(value, formKey, formData)
    } else if (value !== undefined) {
      formData.append(formKey, value)
    }
  }
  return formData
}

const useAuthenticatedResources = (url, options = {}, additionalActions = []) => {
  const { getValidToken } = useAuthentication()

  // Store options in a ref to prevent unstable {} references from
  // cascading through all callbacks and defeating memoization
  const optionsRef = useRef(options)
  optionsRef.current = options

  const getAuthenticatedOptions = useCallback((accessToken) => {
    return merge({}, optionsRef.current, {
      headers: {
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
    })
  }, [])

  const all = useCallback((props = {}) => {
    const { params } = props
    const queryString = toUrlSearchParams(params).toString()
    const fullUrl = compact([url, queryString]).join('?')

    return getValidToken()
      .then((accessToken) => fetchJSON(fullUrl, 'GET', getAuthenticatedOptions(accessToken)))
  }, [url, getValidToken, getAuthenticatedOptions])

  const get = useCallback((id, props = {}) => {
    const { params } = props
    const queryString = toUrlSearchParams(params).toString()
    const fullUrl = compact([[url, id].join('/'), queryString]).join('?')

    return getValidToken()
      .then((accessToken) => fetchJSON(fullUrl, 'GET', getAuthenticatedOptions(accessToken)))
  }, [url, getValidToken, getAuthenticatedOptions])

  const createByJson = useCallback((data) => {
    return getValidToken()
      .then((accessToken) => fetchJSON(url, 'POST', getAuthenticatedOptions(accessToken), data))
  }, [url, getValidToken, getAuthenticatedOptions])

  const createByForm = useCallback((data) => {
    return getValidToken()
      .then((accessToken) => fetchJSON(url, 'POST', getAuthenticatedOptions(accessToken), jsonToFormData(data)))
  }, [url, getValidToken, getAuthenticatedOptions])

  const updateByJson = useCallback((id, data) => {
    const fullUrl = [url, id].join('/')

    return getValidToken()
      .then((accessToken) => fetchJSON(fullUrl, 'PUT', getAuthenticatedOptions(accessToken), data))
  }, [url, getValidToken, getAuthenticatedOptions])

  const updateByForm = useCallback((id, data) => {
    const fullUrl = [url, id].join('/')

    return getValidToken()
      .then((accessToken) => fetchJSON(fullUrl, 'PUT', getAuthenticatedOptions(accessToken), jsonToFormData(data)))
  }, [url, getValidToken, getAuthenticatedOptions])

  const update = options['mode'] === 'form' ? updateByForm : updateByJson
  const create = options['mode'] === 'form' ? createByForm : createByJson

  const destroy = useCallback((id) => {
    const fullUrl = [url, id].join('/')

    return getValidToken()
      .then((accessToken) => fetchJSON(fullUrl, 'DELETE', getAuthenticatedOptions(accessToken)))
  }, [url, getValidToken, getAuthenticatedOptions])

  const methods = useMemo(() => {
    return additionalActions.reduce((acc, action) => {
      acc[action.name] = async (...args) => {
        const path = action.path || action.name;
        const fullUrl = action.member ? [url, args[0], path].join("/") : [url, path].join("/");
        const method = action.method || 'GET';
        const data = ["POST", "PUT", "PATCH"].includes(method.toUpperCase()) ? args[action.member ? 1 : 0] : {};

        return getValidToken()
          .then((accessToken) => fetchJSON(fullUrl, method, getAuthenticatedOptions(accessToken), data));
      };
      return acc;
    }, {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalActions]);

  return { all, get, create, update, destroy, ...methods };
}

export default useAuthenticatedResources
